import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { X, Camera } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/lib/supabase";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";

const INK = "var(--caileve-ink)";
const MUTED = "var(--caileve-muted)";
const LILAC = "var(--caileve-lilac)";
const LILAC_BG = "var(--caileve-soft)";
const GOLD = "var(--caileve-gold)";
const SERIF = "'Cormorant Garamond', Georgia, serif";

// ---------- Shared count hooks ----------

export function useTotalCollectionCount() {
  const { user } = useAuth();
  const uid = user?.id;
  return useQuery({
    queryKey: ["collection_total_count", uid],
    enabled: !!uid,
    throwOnError: false,
    retry: 2,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 4000),
    queryFn: async () => {
      const [j, c] = await Promise.all([
        supabase
          .from("jewelry_items")
          .select("id", { count: "exact", head: true })
          .eq("user_id", uid!),
        supabase
          .from("clothing_items")
          .select("id", { count: "exact", head: true })
          .eq("user_id", uid!),
      ]);
      if (j.error || c.error) {
        throw new Error(j.error?.message || c.error?.message || "collection_count_failed");
      }
      return (j.count ?? 0) + (c.count ?? 0);
    },
  });
}

export function useJewelryOnlyCount() {
  const { user } = useAuth();
  const uid = user?.id;
  return useQuery({
    queryKey: ["jewelry_only_count", uid],
    enabled: !!uid,
    throwOnError: false,
    retry: false,
    queryFn: async () => {
      try {
        const { count } = await supabase
          .from("jewelry_items")
          .select("id", { count: "exact", head: true })
          .eq("user_id", uid!);
        return count ?? 0;
      } catch {
        return undefined as unknown as number;
      }
    },
  });
}

// ---------- Card shell ----------

function PromptCard({
  icon,
  body,
  ctaLabel,
  cta,
  onDismiss,
}: {
  icon?: React.ReactNode;
  body: string;
  ctaLabel: string;
  cta: React.ReactNode;
  onDismiss: () => void;
}) {
  return (
    <div
      className="relative flex items-center gap-3 pl-4 pr-3 py-2 mb-3"
      style={{
        minHeight: 44,
        backgroundColor: "transparent",
        borderLeft: `2px solid ${GOLD}`,
      }}
    >
      {icon && <div className="flex-shrink-0">{icon}</div>}
      <p
        className="flex-1"
        style={{ fontSize: 12, color: INK }}
      >
        {body}
      </p>
      <div className="flex-shrink-0">{cta}</div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        className="flex-shrink-0 hover:opacity-70"
        style={{ color: MUTED, fontSize: 12 }}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function AddJewelryLink({ label }: { label: string }) {
  return (
    <Link
      to="/jewelry"
      search={{ new: 1 }}
      className="whitespace-nowrap hover:opacity-80"
      style={{ fontSize: 12, color: LILAC }}
    >
      {label}
    </Link>
  );
}

function AddAnyPieceCta({ label }: { label: string }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const go = (path: "/jewelry" | "/clothing" | "/accessories") => {
    setOpen(false);
    navigate({ to: path, search: { new: 1 } as never });
  };
  const rows: { emoji: string; label: string; path: "/jewelry" | "/clothing" | "/accessories" }[] = [
    { emoji: "💎", label: "Jewelry", path: "/jewelry" },
    { emoji: "👕", label: "Clothing", path: "/clothing" },
    { emoji: "👜", label: "Accessory", path: "/accessories" },
  ];
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="whitespace-nowrap hover:opacity-80"
        style={{ fontSize: 12, color: LILAC }}
      >
        {label}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="p-0 flex flex-col"
          style={{
            backgroundColor: "var(--caileve-bg)",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          }}
        >
          <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
            <span
              aria-hidden
              style={{
                width: 40,
                height: 4,
                borderRadius: "var(--caileve-radius-pill)",
                backgroundColor: LILAC_BG,
                display: "block",
              }}
            />
          </div>
          <SheetTitle
            className="text-center font-serif-display text-2xl pt-3 pb-5"
            style={{ color: INK, fontFamily: SERIF }}
          >
            What would you like to add?
          </SheetTitle>
          <div className="flex flex-col pb-8">
            {rows.map((r) => (
              <button
                key={r.path}
                type="button"
                onClick={() => go(r.path)}
                className="flex items-center gap-4 px-6 py-4 text-left hover:bg-[var(--caileve-soft)]/40 transition-colors"
                style={{ borderTop: "1px solid var(--caileve-soft)" }}
              >
                <span style={{ fontSize: 22 }}>{r.emoji}</span>
                <span
                  className="font-serif-display"
                  style={{ color: INK, fontFamily: SERIF, fontSize: 20 }}
                >
                  {r.label}
                </span>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// ---------- Contextual prompt (time of day) ----------

function getTimeOfDayPrompt() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12)
    return "What are you wearing today? Add it and Cailève will remember it.";
  if (h >= 12 && h < 18)
    return "Your collection grows one piece at a time. Add what caught your eye today.";
  return "Wearing something special tonight? Add it before you take it off.";
}

export function ContextualPrompt({ onDismissed }: { onDismissed?: () => void } = {}) {
  const { data: total } = useTotalCollectionCount();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("caileve_contextual_prompt_dismissed") === "1") {
      setDismissed(true);
    }
  }, []);

  if (dismissed) return null;
  if (total === undefined) return null;
  if (total >= 30) return null;

  return (
    <PromptCard
      body={getTimeOfDayPrompt()}
      ctaLabel="Capture a piece"
      cta={<AddAnyPieceCta label="Capture a piece" />}
      onDismiss={() => {
        sessionStorage.setItem("caileve_contextual_prompt_dismissed", "1");
        setDismissed(true);
        onDismissed?.();
      }}
    />
  );
}

// ---------- Weekly One Piece prompt ----------

const WEEKLY_PROMPTS = [
  "Start with your most worn piece. The one you reach for without thinking.",
  "Add a piece with a story. The one that came from somewhere special.",
  "Your favorite everyday jewelry. The piece that feels most like you.",
  "Something you haven't worn in a while. Let Cailève help you rediscover it.",
];

function weeksSince(dateIso: string | undefined): number {
  if (!dateIso) return 0;
  const start = new Date(dateIso).getTime();
  if (Number.isNaN(start)) return 0;
  const diffMs = Date.now() - start;
  return Math.max(0, Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000)));
}

export function WeeklyPrompt() {
  const { user } = useAuth();
  const { data: total } = useTotalCollectionCount();
  const weekIndex = weeksSince(user?.created_at);
  const weekNumber = (weekIndex % 4) + 1;
  const storageKey = `caileve_weekly_prompt_dismissed_${weekNumber}`;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(storageKey) === "1") setDismissed(true);
  }, [storageKey]);

  if (dismissed) return null;
  if (total === undefined) return null;
  if (total >= 50) return null;

  return (
    <PromptCard
      icon={<Camera className="h-4 w-4" style={{ color: GOLD }} strokeWidth={1.5} />}
      body={WEEKLY_PROMPTS[weekNumber - 1]}
      ctaLabel="Add this piece"
      cta={<AddJewelryLink label="Add this piece" />}
      onDismiss={() => {
        localStorage.setItem(storageKey, "1");
        setDismissed(true);
      }}
    />
  );
}

// ---------- Account collection summary ----------

function summaryFor(n: number): string | null {
  if (n <= 0) return null;
  const piece = n === 1 ? "piece" : "pieces";
  if (n <= 5) return `Your Cailève holds ${n} ${piece}. Your style story is beginning.`;
  if (n <= 15) return `Your Cailève holds ${n} ${piece}. Cailève is starting to learn your style.`;
  if (n <= 30) return `Your Cailève holds ${n} ${piece}. Your collection is taking shape beautifully.`;
  if (n <= 60) return `Your Cailève holds ${n} ${piece}. Cailève knows you well.`;
  if (n <= 100) return `Your Cailève holds ${n} ${piece}. Your collection is telling a rich story.`;
  return `Your Cailève holds ${n} ${piece}. Your style story is deeply personal.`;
}

export function CollectionSummary({ className = "" }: { className?: string }) {
  const { data: total } = useTotalCollectionCount();
  if (total === undefined) return null;
  const text = summaryFor(total);
  if (!text) return null;
  return (
    <p
      className={`italic text-sm ${className}`}
      style={{ fontFamily: SERIF, color: MUTED }}
    >
      {text}
    </p>
  );
}

// ---------- Near-empty gallery banner ----------

type NearEmptyKind = "jewelry" | "clothing" | "accessory";

const NEAR_EMPTY_COPY: Record<
  NearEmptyKind,
  { primary: string; secondary: string; cta: string }
> = {
  jewelry: {
    primary: "Your jewelry story is just beginning.",
    secondary:
      "What piece are you wearing today? Add it and let Cailève start learning your style.",
    cta: "Capture a piece",
  },
  clothing: {
    primary: "Every great wardrobe starts with one piece.",
    secondary:
      "Add the outfit you feel most yourself in — that's always the right place to begin.",
    cta: "Photograph a garment",
  },
  accessory: {
    primary: "The finishing pieces tell the whole story.",
    secondary:
      "Add an accessory you love and Cailève will begin composing around it.",
    cta: "Capture an accessory",
  },
};

export function NearEmptyBanner({
  kind,
  itemCount,
  onCta,
  crossCount = 0,
}: {
  kind: NearEmptyKind;
  itemCount: number;
  onCta: () => void;
  crossCount?: number;
}) {
  if (itemCount > 0) return null;
  const copy = NEAR_EMPTY_COPY[kind];
  return (
    <div
      className="text-center px-6 py-10 mb-8"
      style={{
        backgroundColor: LILAC_BG,
        borderLeft: `1px solid ${GOLD}`,
      }}
    >
      <p
        className="italic text-2xl"
        style={{ fontFamily: SERIF, color: INK }}
      >
        {copy.primary}
      </p>
      <p
        className="italic text-base mt-3 max-w-md mx-auto leading-relaxed"
        style={{ fontFamily: SERIF, color: MUTED }}
      >
        {crossCount >= 3
          ? kind === "jewelry"
            ? "You have clothing and accessories — add jewelry and Cailève can style complete looks."
            : kind === "clothing"
              ? "You have jewelry — add clothing and Cailève can build full outfits around your pieces."
              : "You have jewelry and clothing — accessories complete the look."
          : copy.secondary}
      </p>
      <button
        type="button"
        onClick={onCta}
        className="mt-6 inline-block px-6 py-2.5 text-xs uppercase tracking-[0.25em] hover:opacity-90"
        style={{ backgroundColor: LILAC, color: "#fff" }}
      >
        {copy.cta}
      </button>
    </div>
  );
}

// ---------- Wearing trigger note (suggest / style this piece) ----------

export function WearingTriggerNote() {
  const { data: jewelryCount } = useJewelryOnlyCount();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (jewelryCount === undefined) return;
    if (jewelryCount >= 10) return;
    if (sessionStorage.getItem("caileve_wearing_trigger_shown") === "1") return;
    sessionStorage.setItem("caileve_wearing_trigger_shown", "1");
    setShow(true);
  }, [jewelryCount]);

  if (!show) return null;

  return (
    <div className="mt-10 text-center">
      <p
        className="italic text-sm max-w-md mx-auto leading-relaxed"
        style={{ fontFamily: SERIF, color: MUTED }}
      >
        Cailève's suggestions grow more personal the more pieces it knows. Add
        what you're wearing today and watch your style story deepen.
      </p>
      <Link
        to="/jewelry"
        search={{ new: 1 }}
        className="inline-block mt-2 text-sm italic hover:opacity-80"
        style={{ fontFamily: SERIF, color: LILAC }}
      >
        Capture a piece
      </Link>
    </div>
  );
}

// ---------- Collection depth indicator ----------

export function CollectionDepthIndicator() {
  const { data: total } = useTotalCollectionCount();

  if (total === undefined) return null;
  if (total >= 15) return null;

  

  let copy = "Your collection is beginning to take shape.";
  if (total >= 10)
    copy =
      "Your style is coming into focus. A few more pieces and Cailève will know you well.";
  else if (total >= 5) copy = "Cailève is beginning to understand your style.";

  return (
    <div className="mb-6 px-1">
      <p
        className="italic text-center mt-3"
        style={{ fontFamily: SERIF, color: MUTED, fontSize: 13 }}
      >
        {copy}
      </p>
    </div>
  );
}

// ---------- Forgotten piece prompt ----------

export function ForgottenPiecePrompt() {
  const { user } = useAuth();
  const uid = user?.id;
  const { data: total } = useTotalCollectionCount();
  const [dismissed, setDismissed] = useState(false);

  const weekNumber =
    Math.floor(
      (Date.now() - new Date(user?.created_at ?? Date.now()).getTime()) /
        (7 * 24 * 60 * 60 * 1000),
    ) % 52;
  const storageKey = `caileve_forgotten_piece_dismissed_w${weekNumber}`;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(storageKey) === "1") setDismissed(true);
  }, [storageKey]);

  const { data: piece } = useQuery({
    queryKey: ["forgotten_piece", uid],
    enabled: !!uid && !dismissed && (total ?? 0) >= 15,
    staleTime: 30 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      try {
        const { data } = await supabase
          .from("jewelry_items")
          .select("id, name")
          .eq("user_id", uid!)
          .eq("is_wishlist", false)
          .eq("is_accessory", false)
          .order("updated_at", { ascending: true })
          .limit(1)
          .maybeSingle();
        return data ?? null;
      } catch {
        return null;
      }
    },
  });

  if (dismissed) return null;
  if (!piece) return null;
  if ((total ?? 0) < 15) return null;

  return (
    <PromptCard
      body={`Remember your ${piece.name}? It's been a while.`}
      ctaLabel="Style it"
      cta={
        <Link
          to="/jewelry/$id"
          params={{ id: piece.id }}
          className="whitespace-nowrap hover:opacity-80"
          style={{ fontSize: 12, color: LILAC }}
        >
          Style it
        </Link>
      }
      onDismiss={() => {
        localStorage.setItem(storageKey, "1");
        setDismissed(true);
      }}
    />
  );
}

// ---------- Considering prompt ----------

export function ConsideringPrompt() {
  const { user } = useAuth();
  const uid = user?.id;
  const { data: total } = useTotalCollectionCount();
  const [dismissed, setDismissed] = useState(false);
  const storageKey = "caileve_considering_prompt_dismissed_v1";

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(storageKey) === "1") setDismissed(true);
  }, []);

  const { data: piece } = useQuery({
    queryKey: ["considering_prompt_piece", uid],
    enabled: !!uid && !dismissed && (total ?? 0) >= 15,
    staleTime: 10 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      try {
        const { data } = await supabase
          .from("jewelry_items")
          .select("id, name")
          .eq("user_id", uid!)
          .eq("is_wishlist", true)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        return data ?? null;
      } catch {
        return null;
      }
    },
  });

  if (dismissed) return null;
  if (!piece) return null;
  if ((total ?? 0) < 15) return null;

  return (
    <PromptCard
      body={`Still thinking about ${piece.name}?`}
      ctaLabel="Take another look"
      cta={
        <Link
          to="/jewelry/$id"
          params={{ id: piece.id }}
          className="whitespace-nowrap hover:opacity-80"
          style={{ fontSize: 12, color: LILAC }}
        >
          Take another look
        </Link>
      }
      onDismiss={() => {
        localStorage.setItem(storageKey, "1");
        setDismissed(true);
      }}
    />
  );
}